import React, { useCallback, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { ChevronRight } from "lucide-react";
import { ONEHUB_BRAND_FORM_CONTROLS_CSS, ONEHUB_BRAND_HEX } from "./brandFormControlsCss";
import {
  normalizeAppManagementPermissions,
  permissionModulePrefixForSection,
} from "../../utils/normalizeAppManagementPermissions";

/** Fallback ordering when API does not send `domains` (Casbin module keys). Align with product UX. */
const REGISTRY_MODULE_ORDER = [
  "roles",
  "users",
  "settings",
  "products",
  "categories",
  "orders",
  "catalog",
  "inventory",
  "app_management",
  "subscriptions",
  "trackhub_settings",
  "shipments",
  "dashboard",
  "sales_reports",
  "order_reports",
  "shipment_reports",
  "menus",
  "reports",
  "printer_settings",
];

/**
 * API domain `id` order for permission accordions (matches `TENANT_DOMAINS` in backend catalog).
 * Unknown ids sort after this list.
 */
const DOMAIN_SECTION_ORDER = [
  "role_management",
  "user_management",
  "stores_branches_management",
  "product_management",
  "categories_management",
  "orders_management",
  "catalog_management",
  "inventory_management",
  "app_management",
  "subscriptions",
  "trackhub_settings",
  "manual_shipments",
  "dashboard",
  "sales_reports",
  "order_reports",
  "shipment_reports",
  "printer_settings",
  "payments",
];

/** Labels for registry-only fallback (API-driven domains carry their own labels). */
const REGISTRY_FALLBACK_ACTION_LABELS = {
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete",
  refund: "Refund",
  export: "Export",
  invite: "Invite",
  import: "Import",
  sync: "Sync",
  view_apps: "View Third Party Integrations/Add-ons",
  view_integrations: "View integrations",
  add_integrations: "Add integrations",
  disconnect_integration: "Disconnect integrations",
  manage_auto_access_orders: "Manage auto accept orders",
  manage_branch_mappings: "Manage branch mappings",
  manage_addon_settings: "Manage add-on settings",
  enable_disable_inventory: "Enable/disable Auto Accept inventory",
  view_subscription: "View Subscription",
  perform_subscription: "Perform Subscription",
  cancel_subscription: "Cancel Subscriptions",
  cancel: "Cancel",
};

const APP_MANAGEMENT_LEGACY_KEYS = new Set([
  "app_management:view",
  "app_management:create",
  "app_management:update",
  "app_management:delete",
]);

/** Keys excluded from the role wizard accordion (still valid on roles / Casbin). */
function isHiddenCatalogPermissionKey(key) {
  if (!key || typeof key !== "string") return true;
  if (key === "users:invite") return true;
  if (APP_MANAGEMENT_LEGACY_KEYS.has(key)) return true;
  const colon = key.indexOf(":");
  const mod = colon > 0 ? key.slice(0, colon) : key;
  return mod === "payments";
}

/** Grey horizontal bar for tri-state section toggles (neutral, not brand primary). */
const INDETERMINATE_GREY_SVG = encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='%235c636a' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/></svg>"
);

const FIGMA_STYLES = `
.permission-accordion-figma {
  font-family: Inter, sans-serif;
}
.permission-accordion-figma .permission-accordion-toolbar {
  margin-bottom: 0.75rem;
}
.permission-accordion-figma .permission-accordion-section-shell {
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  /* Header: switch column width (detail rows use auto checkbox width + tight label gap). */
  --perm-control-col: 2.75rem;
}
.permission-accordion-figma .permission-accordion-section-head {
  min-height: 52px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  column-gap: 0.35rem;
}
.permission-accordion-figma .permission-accordion-section-head .perm-section-switch {
  flex: 0 0 var(--perm-control-col);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
}
.permission-accordion-figma .permission-accordion-section-head .permission-accordion-section-expand {
  flex: 1 1 auto;
  min-width: 0;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  align-self: center;
  line-height: 1.3;
  padding-block: 0;
  text-transform: none;
}
.permission-accordion-figma .permission-accordion-section-expand .fw-semibold {
  text-transform: none;
}
.permission-accordion-figma .permission-accordion-section-head .perm-section-switch .switch-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.permission-accordion-figma .permission-accordion-details {
  border-top: 1px solid #eef2f6;
  background: #fafbfc;
  padding-block: 0.75rem 1rem;
  padding-inline: 1rem;
}
.permission-accordion-figma .perm-detail-row {
  display: grid;
  grid-template-columns: var(--perm-control-col) minmax(0, 1fr);
  column-gap: 0.35rem;
  align-items: center;
}
.permission-accordion-figma .perm-detail-control-col {
  display: flex;
  justify-content: center;
  align-items: center;
}
.permission-accordion-figma .perm-detail-label-col {
  min-width: 0;
  text-transform: none;
}
/* Bootstrap indeterminate uses component-active (brand) background; use neutral grey instead. */
.permission-accordion-figma .perm-section-switch.is-partial .form-check-input:indeterminate {
  background-color: #dee2e6 !important;
  border-color: #ced4da !important;
  --bs-form-check-bg-image: url("data:image/svg+xml,${INDETERMINATE_GREY_SVG}");
}
.permission-accordion-figma .perm-section-switch.is-partial .form-check-input:indeterminate:focus {
  border-color: #ced4da;
  box-shadow: 0 0 0 0.2rem rgba(206, 212, 218, 0.45);
}
.permission-accordion-figma .perm-section-switch-switch .form-switch .form-check-input {
  cursor: pointer;
}
.permission-accordion-figma .perm-section-switch-switch .form-switch .form-check-input:disabled {
  cursor: not-allowed;
}
/* Select-all bar: keep partial (indeterminate) neutral, not brand blue. */
.permission-accordion-figma .perm-select-all-check .form-check-input:indeterminate {
  background-color: #dee2e6 !important;
  border-color: #ced4da !important;
  --bs-form-check-bg-image: url("data:image/svg+xml,${INDETERMINATE_GREY_SVG}");
}
.permission-accordion-figma .perm-select-all-check .form-check-input:indeterminate:focus {
  border-color: #ced4da;
  box-shadow: 0 0 0 0.2rem rgba(206, 212, 218, 0.45);
}
.permission-accordion-figma.onehub-brand-form-controls .perm-select-all-check.form-check .form-check-input:checked {
  background-color: ${ONEHUB_BRAND_HEX} !important;
  border-color: ${ONEHUB_BRAND_HEX} !important;
}
.permission-accordion-figma.onehub-brand-form-controls
  .permission-accordion-details
  .perm-detail-row
  .perm-detail-control-col
  input[type="checkbox"]:checked {
  background-color: ${ONEHUB_BRAND_HEX} !important;
  border-color: ${ONEHUB_BRAND_HEX} !important;
}

/* Section switches: MDB ::after thumbs are overridden globally via onehub-brand-form-controls + brandFormControlsCss. */
.permission-accordion-figma.onehub-brand-form-controls
  .permission-accordion-section-head
  .perm-section-switch-switch
  .form-switch
  .form-check-input:checked:focus-visible {
  box-shadow: 0 0 0 0.25rem rgba(8, 59, 76, 0.28) !important;
}

/* Summary strip: "N of M permissions" (design bar — #FBF9F5 / #F6EDDF / #303030 / dot #A3A3A3). */
.permission-accordion-figma .permission-accordion-summary {
  background-color: #fbf9f5;
  border: 0.5px solid #f6eddf;
  border-radius: 4px;
  color: #303030;
  font-size: 0.875rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.35;
}
.permission-accordion-figma .permission-accordion-summary-dot {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  background-color: #a3a3a3 !important;
}
`;

const PERMISSION_ACCORDION_STYLES = `${ONEHUB_BRAND_FORM_CONTROLS_CSS}\n${FIGMA_STYLES}`;

export function orderedModules(registry) {
  const keys = Object.keys(registry || {}).filter((m) => m !== "payments");
  const ordered = REGISTRY_MODULE_ORDER.filter((m) => keys.includes(m));
  const rest = keys.filter((k) => !REGISTRY_MODULE_ORDER.includes(k)).sort();
  return [...ordered, ...rest];
}

export function flattenRegistryKeys(registry) {
  const keys = [];
  for (const mod of orderedModules(registry)) {
    const actions = registry[mod] || [];
    for (const act of actions) {
      const k = `${mod}:${act}`;
      if (!isHiddenCatalogPermissionKey(k)) keys.push(k);
    }
  }
  return keys;
}

function moduleKeys(registry, mod) {
  const actions = registry[mod] || [];
  return actions.map((act) => `${mod}:${act}`);
}

/**
 * When any `module:action` is selected (action !== `view`) and the catalog includes
 * `module:view`, ensure that view key is selected. Applies to all modules in the registry.
 * @param {string[]} selectedArr
 * @param {Set<string>|Iterable<string>} allowedKeys - full catalog keys (e.g. from domains or registry)
 * @returns {string[]}
 */
/**
 * App management implicit read scopes:
 * - add_integrations → view_integrations + view_apps (catalog / add-ons)
 * - disconnect_integration → view_integrations only
 * @param {string[]} selectedArr
 * @param {Set<string>|Iterable<string>} allowedKeys
 * @returns {string[]}
 */
/** Stores & branches — granular branch ops require `settings:update`; update can stand alone. */
export const SETTINGS_BRANCH_OPERATION_KEYS = [
  "settings:manage_auto_access_orders",
  "settings:manage_branch_mappings",
  "settings:manage_addon_settings",
];

export const SETTINGS_UPDATE_KEY = "settings:update";

/**
 * When any branch-operation permission is selected, ensure `settings:update`.
 * When `settings:update` is cleared, remove all branch-operation permissions.
 * @param {string[]} selectedArr
 * @param {Set<string>|Iterable<string>} allowedKeys
 * @returns {string[]}
 */
export function ensureSettingsBranchOperationDependencies(selectedArr, allowedKeys) {
  const allowed = allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys || []);
  const set = new Set(selectedArr || []);
  const granularKeys = SETTINGS_BRANCH_OPERATION_KEYS.filter((k) => allowed.has(k));
  const updateAllowed = allowed.has(SETTINGS_UPDATE_KEY);

  if (updateAllowed && granularKeys.some((k) => set.has(k))) {
    set.add(SETTINGS_UPDATE_KEY);
  }

  if (!updateAllowed || !set.has(SETTINGS_UPDATE_KEY)) {
    for (const k of granularKeys) {
      set.delete(k);
    }
  }

  return [...set];
}

/** @param {string} key */
export function stripSettingsBranchOpsWhenUpdateRemoved(key, selectedList) {
  if (key !== SETTINGS_UPDATE_KEY) return selectedList;
  return (selectedList || []).filter(
    (k) => !SETTINGS_BRANCH_OPERATION_KEYS.includes(k)
  );
}

export function ensureImplicitAppManagementDependencies(selectedArr, allowedKeys) {
  const allowed = allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys || []);
  const set = new Set(selectedArr || []);
  const add = (key) => {
    if (allowed.has(key)) set.add(key);
  };
  if (set.has("app_management:disconnect_integration")) {
    add("app_management:view_integrations");
  }
  if (set.has("app_management:add_integrations")) {
    add("app_management:view_integrations");
    add("app_management:view_apps");
  }
  return [...set];
}

export function ensureImplicitViewForSelected(selectedArr, allowedKeys) {
  const allowed = allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys || []);
  const set = new Set(selectedArr || []);
  for (const k of [...set]) {
    const i = k.lastIndexOf(":");
    if (i <= 0) continue;
    const mod = k.slice(0, i);
    const action = k.slice(i + 1);
    if (action === "view") continue;
    if (mod === "app_management") continue;
    const viewKey = `${mod}:view`;
    if (allowed.has(viewKey)) {
      set.add(viewKey);
    }
  }
  return ensureImplicitAppManagementDependencies([...set], allowed);
}

/**
 * Full wizard selection reconcile: implicit views, app-management reads, settings branch ops.
 * @param {string[]} selectedArr
 * @param {Set<string>|Iterable<string>} allowedKeys
 * @returns {string[]}
 */
export function reconcilePermissionSelection(selectedArr, allowedKeys) {
  const allowed = allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys || []);
  return ensureSettingsBranchOperationDependencies(
    ensureImplicitViewForSelected(selectedArr, allowed),
    allowed
  );
}

function uniquePermissionKeysFromDomains(domains) {
  const seen = new Set();
  const out = [];
  for (const d of domains || []) {
    const entries = d.entries?.length ? d.entries : (d.permissionKeys || []).map((key) => ({ key, label: key }));
    for (const e of entries) {
      const k = e.key;
      if (!k || seen.has(k) || isHiddenCatalogPermissionKey(k)) continue;
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

function actionLabelFromKey(key, t) {
  const act = key.split(":").pop();
  return t(`rbac.permissionAction.${act}`, {
    defaultValue: REGISTRY_FALLBACK_ACTION_LABELS[act] || act,
  });
}

function sortSectionRowsByDomainOrder(rows) {
  const rank = (id) => {
    const i = DOMAIN_SECTION_ORDER.indexOf(id);
    return i === -1 ? DOMAIN_SECTION_ORDER.length : i;
  };
  return [...(rows || [])].sort((a, b) => {
    const da = rank(a.id);
    const db = rank(b.id);
    if (da !== db) return da - db;
    return String(a.id).localeCompare(String(b.id));
  });
}

function sectionRowsFromDomains(domains, t) {
  const rows = [];
  for (const d of domains || []) {
    const rawEntries = d.entries?.length
      ? d.entries
      : (d.permissionKeys || []).map((key) => ({
          key,
          label: actionLabelFromKey(key, t),
        }));
    const entries = [];
    const seen = new Set();
    for (const e of rawEntries) {
      if (!e.key || seen.has(e.key) || isHiddenCatalogPermissionKey(e.key)) continue;
      seen.add(e.key);
      entries.push({
        key: e.key,
        label: actionLabelFromKey(e.key, t),
      });
    }
    if (entries.length === 0) continue;
    const keys = entries.map((e) => e.key);
    const domainId = d.id || "";
    const apiLabel = d.label || domainId;
    rows.push({
      id: domainId,
      label: t(`rbac.permissionDomain.${domainId}`, {
        defaultValue: apiLabel,
      }),
      keys,
      entries,
    });
  }
  return rows;
}

function sectionRowsFromRegistry(registry, t) {
  return orderedModules(registry).map((mod) => {
      const keys = moduleKeys(registry, mod).filter((k) => !isHiddenCatalogPermissionKey(k));
      if (keys.length === 0) return null;
      const entries = keys.map((key) => ({
        key,
        label: actionLabelFromKey(key, t),
      }));
      const moduleLabelFallback = mod.charAt(0).toUpperCase() + mod.slice(1);
      return {
        id: mod,
        label: t(`rbac.permissionModule.${mod}`, {
          defaultValue: moduleLabelFallback,
        }),
        keys,
        entries,
      };
    })
    .filter(Boolean);
}

/**
 * Expandable permission groups with select-all, section switches, and action checkboxes.
 * Prefer `domains` from GET `/v1/permissions-registry` (labels + grouping from backend catalog).
 * `t` is i18next translate function (or pass String passthrough).
 */
export default function PermissionAccordion({
  domains,
  registry,
  selected,
  onChangeSelected,
  expanded,
  onToggleExpand,
  disabled = false,
  dir = "ltr",
  t = (s) => s,
  /** When nested under Bootstrap form rows, drop extra horizontal padding and tighten detail indent. */
  embeddedInForm = false,
}) {
  const rtl = dir === "rtl";
  const normalizedSelected = useMemo(
    () => normalizeAppManagementPermissions(selected || []),
    [selected]
  );
  const selectedSet = useMemo(() => new Set(normalizedSelected), [normalizedSelected]);

  const useDomains = Array.isArray(domains) && domains.length > 0;
  const sections = useMemo(() => {
    if (useDomains) return sortSectionRowsByDomainOrder(sectionRowsFromDomains(domains, t));
    return sectionRowsFromRegistry(registry || {}, t);
  }, [useDomains, domains, registry, t]);

  const allKeys = useMemo(() => {
    if (useDomains) return uniquePermissionKeysFromDomains(domains);
    return flattenRegistryKeys(registry || {});
  }, [useDomains, domains, registry]);

  const allKeysSet = useMemo(() => new Set(allKeys), [allKeys]);

  const visibleSelectedCount = useMemo(
    () => normalizedSelected.filter((k) => allKeysSet.has(k)).length,
    [normalizedSelected, allKeysSet]
  );

  const totalAll = allKeys.length;
  const allSelected = totalAll > 0 && allKeys.every((k) => selectedSet.has(k));
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  const applySelectionReconcile = useCallback(
    (next) =>
      normalizeAppManagementPermissions(reconcilePermissionSelection(next, allKeysSet)),
    [allKeysSet]
  );

  useEffect(() => {
    const reconciled = applySelectionReconcile(selected || []);
    const current = (selected || []).slice().sort();
    if (reconciled.join("\0") !== current.join("\0")) {
      onChangeSelected(reconciled);
    }
  }, [selected, onChangeSelected, applySelectionReconcile]);

  const setSelectAll = (on) => {
    if (disabled) return;
    if (on) {
      onChangeSelected(applySelectionReconcile([...allKeys]));
    } else {
      onChangeSelected([]);
    }
  };

  const chevronBaseRotate = rtl ? "scaleX(-1)" : "none";

  return (
    <div
      className={`permission-accordion-figma onehub-brand-form-controls${embeddedInForm ? " permission-accordion--embedded-form" : ""}${
        disabled ? " opacity-65" : ""
      }`}
      dir={dir}
      aria-disabled={disabled || undefined}
    >
      <style>{PERMISSION_ACCORDION_STYLES}</style>

      <div
        className={`permission-accordion-toolbar d-flex align-items-center justify-content-between gap-3 ${
          embeddedInForm ? "px-0" : "px-1"
        }`}
      >
        <span className="fw-bold small text-dark letter-spacing-wide">
          {t("rbac.permissionsHeading")}
        </span>
        <div className="d-flex align-items-center gap-1 small mb-0">
          <input
            type="checkbox"
            id="perm-select-all"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={(e) => setSelectAll(e.target.checked)}
            disabled={disabled}
            style={{
              width: 16,
              height: 16,
              accentColor: ONEHUB_BRAND_HEX,
              cursor: disabled ? "not-allowed" : "pointer",
              margin: 0,
              opacity: disabled ? 0.6 : 1,
            }}
          />
          <label
            htmlFor="perm-select-all"
            className="text-dark mb-0"
            style={{
              cursor: disabled ? "not-allowed" : "pointer",
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {t("rbac.selectAll")}
          </label>
        </div>
      </div>

      <div className={`d-flex flex-column gap-2 ${embeddedInForm ? "px-0" : "px-1"}`}>
        {sections.map((section) => {
          const { id, label, keys, entries } = section;
          const n = keys.length;
          const modulePrefix = permissionModulePrefixForSection(id);
          const allSec = n > 0 && keys.every((k) => selectedSet.has(k));
          const someSec = keys.some((k) => selectedSet.has(k)) && !allSec;
          const isOpen = expanded.has(id);

          const toggleSectionAll = () => {
            if (disabled) return;
            let next;
            if (allSec) {
              if (modulePrefix) {
                next = selected.filter((k) => !k.startsWith(modulePrefix));
              } else {
                next = selected.filter((k) => !keys.includes(k));
              }
            } else {
              next = [...new Set([...selected, ...keys])];
            }
            onChangeSelected(applySelectionReconcile(next));
          };

          const toggleKey = (key) => {
            if (disabled) return;
            let next;
            if (selectedSet.has(key)) {
              next = selected.filter((k) => k !== key);
              next = stripSettingsBranchOpsWhenUpdateRemoved(key, next);
            } else {
              next = [...selected, key];
            }
            onChangeSelected(applySelectionReconcile(next));
          };

          const expandBtn = (
            <button
              type="button"
              className={`permission-accordion-section-expand btn btn-link text-dark text-decoration-none p-0 d-flex align-items-center justify-content-between gap-2 min-w-0`}
              onClick={() => onToggleExpand(id)}
            >
              <span className="fw-semibold text-truncate" style={{ lineHeight: 1.3 }}>{label}</span>
              <span className="d-inline-flex align-items-center gap-2 flex-shrink-0">
                <span className="text-muted small">{t("rbac.permissionsCount", { count: n })}</span>
                <ChevronRight
                  size={18}
                  aria-hidden
                  className="text-secondary flex-shrink-0 transition-transform"
                  style={{
                    transform: isOpen
                      ? rtl
                        ? "scaleX(-1) rotate(90deg)"
                        : "rotate(90deg)"
                      : chevronBaseRotate,
                    transition: "transform 0.2s",
                  }}
                />
              </span>
            </button>
          );

          return (
            <div key={id} className="permission-accordion-section-shell">
              <div className={`permission-accordion-section-head px-3 py-2`}>
                <div
                  className={`perm-section-switch flex-shrink-0 ${someSec ? "is-partial" : ""} perm-section-switch-switch`}
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <label className="switch-container m-0" htmlFor={`perm-section-${id}`} style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                    <input
                      type="checkbox"
                      id={`perm-section-${id}`}
                      checked={allSec}
                      ref={(el) => {
                        if (el) el.indeterminate = someSec;
                      }}
                      onChange={toggleSectionAll}
                      disabled={disabled}
                      aria-label={t("rbac.permissionsSectionBulkAria", { module: label })}
                      style={{ display: "none" }}
                    />
                    <span
                      className="switch-slider"
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: 33,
                        height: 18,
                        backgroundColor: allSec ? ONEHUB_BRAND_HEX : (someSec ? "#6b7280" : "#ccc"),
                        borderRadius: 9,
                        transition: "background-color 0.2s ease",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      <span
                        className="switch-thumb"
                        style={{
                          position: "absolute",
                          top: 1.5,
                          left: allSec ? 16.5 : 1.5,
                          width: 15,
                          height: 15,
                          backgroundColor: "#fff",
                          borderRadius: "50%",
                          transition: "left 0.2s ease",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                        }}
                      />
                    </span>
                  </label>
                </div>
                {expandBtn}
              </div>
              {isOpen && (
                <div className="permission-accordion-details">
                  <div className="permission-accordion-details-rows d-flex flex-column gap-2">
                    {entries.map((row) => {
                      const safeKey = String(row.key).replace(/:/g, "-");
                      return (
                        <div key={`${id}-${row.key}`} className="perm-detail-row small mb-0">
                          <div className="perm-detail-control-col">
                            <input
                              type="checkbox"
                              id={`perm-checkbox-${id}-${safeKey}`}
                              checked={selectedSet.has(row.key)}
                              onChange={() => toggleKey(row.key)}
                              disabled={disabled}
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: ONEHUB_BRAND_HEX,
                                cursor: disabled ? "not-allowed" : "pointer",
                                margin: 0,
                                opacity: disabled ? 0.6 : 1,
                              }}
                            />
                          </div>
                          <label
                            htmlFor={`perm-checkbox-${id}-${safeKey}`}
                            className="perm-detail-label-col text-dark small mb-0"
                            style={{
                              cursor: disabled ? "not-allowed" : "pointer",
                              fontFamily: "Inter, sans-serif",
                              fontSize: 14,
                              lineHeight: 1.35,
                              display: "flex",
                              alignItems: "center",
                              opacity: disabled ? 0.6 : 1,
                            }}
                          >
                            {row.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className={`permission-accordion-summary d-flex align-items-center gap-2 px-3 py-2 mt-2 ${
          embeddedInForm ? "mx-0" : "mx-1"
        }`}
      >
        <span
          className="permission-accordion-summary-dot d-inline-block rounded-circle"
          aria-hidden
        />
        <span>
          {t("rbac.permissionsSelectedProgress", {
            selected: visibleSelectedCount,
            total: totalAll,
          })}
        </span>
      </div>
    </div>
  );
}
